/* Resilient axios helper for admin-panel calls.
 *
 * The default axios behaviour swallows a single 502/503/timeout into a
 * silent ".catch() → setData(null)", which is exactly the failure mode
 * that produced 'Operational checks failed to load' / infinite spinners
 * in production. This helper:
 *
 *   • Retries idempotent GETs up to 3 attempts (exponential backoff)
 *     for 5xx, 502, 503, 504, network-error, and timeout.
 *   • Does NOT retry on 401/403/404/400 — those are real errors that
 *     should surface immediately (e.g. re-login required).
 *   • Surfaces a normalised { ok, data, error } shape so panels can
 *     render an explicit retry CTA instead of a blank card.
 *
 * Usage:
 *   const { ok, data, error, status } = await adminGet('/admin/sample-design');
 *   if (!ok) renderRetry(error, status); else renderData(data);
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const RETRYABLE_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504]);

function shouldRetry(err) {
  if (!err) return false;
  if (err.code === "ECONNABORTED") return true; // timeout
  const status = err.response?.status ?? 0;
  return RETRYABLE_STATUS.has(status);
}

export async function adminGet(path, { attempts = 3, baseDelay = 400, axiosOptions = {} } = {}) {
  let lastErr = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const { data, status } = await axios.get(
        `${API}${path}`,
        { withCredentials: true, timeout: 15000, ...axiosOptions },
      );
      return { ok: true, data, status, attempt: i + 1 };
    } catch (err) {
      lastErr = err;
      // Bail immediately on a non-retryable response.
      if (!shouldRetry(err)) break;
      // Exponential backoff with light jitter.
      const jitter = Math.floor(Math.random() * 120);
      await sleep(baseDelay * 2 ** i + jitter);
    }
  }
  const status = lastErr?.response?.status ?? 0;
  const detail =
    lastErr?.response?.data?.detail ||
    (status === 0 ? "Network error or backend unreachable" : lastErr?.message) ||
    "Request failed";
  return { ok: false, data: null, status, error: detail, attempt: attempts };
}

export async function adminPut(path, body, { attempts = 1, axiosOptions = {} } = {}) {
  let lastErr = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const { data, status } = await axios.put(
        `${API}${path}`,
        body,
        { withCredentials: true, timeout: 15000, ...axiosOptions },
      );
      return { ok: true, data, status, attempt: i + 1 };
    } catch (err) {
      lastErr = err;
      if (!shouldRetry(err)) break;
      await sleep(400 * 2 ** i);
    }
  }
  const status = lastErr?.response?.status ?? 0;
  return {
    ok: false,
    data: null,
    status,
    error:
      lastErr?.response?.data?.detail ||
      lastErr?.message ||
      "Save failed",
  };
}

export async function adminPost(path, body, { attempts = 1, axiosOptions = {} } = {}) {
  let lastErr = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const { data, status } = await axios.post(
        `${API}${path}`,
        body,
        { withCredentials: true, timeout: 20000, ...axiosOptions },
      );
      return { ok: true, data, status, attempt: i + 1 };
    } catch (err) {
      lastErr = err;
      if (!shouldRetry(err)) break;
      await sleep(400 * 2 ** i);
    }
  }
  const status = lastErr?.response?.status ?? 0;
  return {
    ok: false,
    data: null,
    status,
    error:
      lastErr?.response?.data?.detail ||
      lastErr?.message ||
      "Request failed",
  };
}
